import {
  formatDateDE,
  jobPlaceLine,
  userInitials,
  type JobSummary,
  type User,
} from "@vidario/domain";
import { Avatar, JobStatusBadge, ProcessStepper } from "@vidario/ui";
import { ChevronRight } from "lucide-react";
import { type ReactElement } from "react";

export interface JobTableProps {
  jobs: readonly JobSummary[];
  isAdmin: boolean;
  creators?: ReadonlyMap<string, User>;
  onOpen: (jobId: string) => void;
}

/** Compact table view of the Dashboard. Same data as JobCard, denser
 * layout. Admin view swaps the Standort column for Recruiter (matching
 * the prototype). */
export function JobTable({ jobs, isAdmin, creators, onOpen }: JobTableProps): ReactElement {
  return (
    <div className="overflow-hidden rounded-xl border border-border-default bg-neutral-0 shadow-sm">
      {/* Narrow screens scroll the table sideways inside the border. */}
      <div
        role="region"
        aria-label="Stellen als Tabelle"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll area with nothing focusable inside must take focus itself, or the keyboard can't scroll it
        tabIndex={0}
        className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-neutral-50 text-left">
              <Th>Stelle</Th>
              <Th>Status</Th>
              <Th>Kandidaten</Th>
              <Th>Fortschritt</Th>
              <Th>{isAdmin ? "Recruiter" : "Standort"}</Th>
              <Th>Eröffnet</Th>
              <Th aria-label="Navigation" />
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const creator = creators?.get(j.createdBy);
              return (
                <tr
                  key={j.id}
                  onClick={() => onOpen(j.id)}
                  className="cursor-pointer border-b border-border-default last:border-b-0 transition-colors hover:bg-neutral-50 focus-within:bg-neutral-50"
                  data-testid={`job-row-${j.id}`}
                >
                  <Td>
                    <div className="font-semibold">{j.title}</div>
                    <div className="text-xs text-text-tertiary">{j.company}</div>
                  </Td>
                  <Td>
                    <JobStatusBadge status={j.status} />
                  </Td>
                  <Td className="tabular-nums font-semibold">{j.candidateCount}</Td>
                  <Td>
                    <div className="w-50">
                      <ProcessStepper currentStep={j.currentStep} size="sm" />
                    </div>
                  </Td>
                  <Td>
                    {isAdmin ? (
                      <span className="inline-flex items-center gap-2">
                        <Avatar initials={creator ? userInitials(creator.name) : "?"} size={22} />
                        <span className="text-text-secondary">{creator?.name ?? "—"}</span>
                      </span>
                    ) : (
                      <span className="text-text-secondary">
                        {jobPlaceLine(j.location, j.workModel, { short: true })}
                      </span>
                    )}
                  </Td>
                  <Td className="tabular-nums text-text-tertiary">{formatDateDE(j.createdAt)}</Td>
                  <Td className="text-right">
                    <ChevronRight size={16} className="text-neutral-400" />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, ...rest }: React.ThHTMLAttributes<HTMLTableCellElement>): ReactElement {
  return (
    <th
      className="border-b border-border-default px-4 py-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary"
      {...rest}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}): ReactElement {
  return <td className={`px-4 py-3.5 ${className ?? ""}`}>{children}</td>;
}
